import React, { useEffect } from 'react';

// Global PWA Install Prompt Handler
window.addEventListener('beforeinstallprompt', (e) => {
    // Prevent the mini-infobar from appearing on mobile
    e.preventDefault();
    // Stash the event so it can be triggered later.
    window.deferredPrompt = e;
});
import { BrowserRouter, Routes, Route, Navigate, NavLink, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AlertProvider, LoadingOverlay, WhatsAppFloat } from './components';
import {
    LoginScreen,
    HomeScreen,
    ProductsScreen,
    ProfileScreen,
    RechargeScreen,
    WithdrawScreen,
    TransactionsScreen,
    BankDetailsScreen,
    SecurityScreen,
    MyProductsScreen,
    ReferralScreen,
    GiftCodeScreen,
    SupportScreen,
    AboutScreen,
    NotificationsScreen,
    TermsScreen,
    PrivacyScreen,
    RefundScreen,
} from './screens';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading, initializing } = useAuth();

    if (initializing || loading) {
        return <LoadingOverlay visible message="Loading..." />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return children;
};

// Public Route Component (redirects to home if authenticated)
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading, initializing } = useAuth();

    if (initializing || loading) {
        return <LoadingOverlay visible message="Loading..." />;
    }

    if (isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    return children;
};

// Main Layout with Bottom Navigation
const MainLayout = () => {
    return (
        <div className="main-layout">
            <main className="main-content">
                <Outlet />
            </main>
            <WhatsAppFloat />
            <nav className="bottom-nav">
                <NavLink to="/" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} end>
                    <span className="material-icons">home</span>
                    <span className="nav-label">HOME</span>
                </NavLink>
                <NavLink to="/products" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                    <span className="material-icons">desktop_windows</span>
                    <span className="nav-label">PRODUCT</span>
                </NavLink>
                <NavLink to="/referral" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                    <span className="material-icons">groups</span>
                    <span className="nav-label">TEAM</span>
                </NavLink>
                <NavLink to="/profile" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                    <span className="material-icons">person</span>
                    <span className="nav-label">ME</span>
                </NavLink>
            </nav>
        </div>
    );
};

function AppContent() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <LoginScreen />
                    </PublicRoute>
                }
            />
            <Route
                path="/signup"
                element={
                    <PublicRoute>
                        <LoginScreen />
                    </PublicRoute>
                }
            />

            {/* All Protected Routes with Bottom Nav */}
            <Route
                element={
                    <ProtectedRoute>
                        <MainLayout />
                    </ProtectedRoute>
                }
            >
                <Route path="/" element={<HomeScreen />} />
                <Route path="/products" element={<ProductsScreen />} />
                <Route path="/my-products" element={<MyProductsScreen />} />
                <Route path="/referral" element={<ReferralScreen />} />
                <Route path="/profile" element={<ProfileScreen />} />
                <Route path="/terms" element={<TermsScreen />} />
                <Route path="/privacy" element={<PrivacyScreen />} />
                <Route path="/refund" element={<RefundScreen />} />
                <Route path="/recharge" element={<RechargeScreen />} />
                <Route path="/withdraw" element={<WithdrawScreen />} />
                <Route path="/transactions" element={<TransactionsScreen />} />
                <Route path="/bank-details" element={<BankDetailsScreen />} />
                <Route path="/security" element={<SecurityScreen />} />
                <Route path="/gift-code" element={<GiftCodeScreen />} />
                <Route path="/support" element={<SupportScreen />} />
                <Route path="/about" element={<AboutScreen />} />
                <Route path="/notifications" element={<NotificationsScreen />} />
            </Route>

            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <AlertProvider>
                    <div className="app">
                        <AppContent />
                    </div>
                </AlertProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
